import { useCallback, useRef } from 'react';
/**
 * A reusable hook that helps dismiss modals, popovers, or overlays only when
 * a pointer interaction occurs entirely outside a specified element.
 *
 * On desktop (or whenever `disableGuard`) is false, ensures the pointer interaction
 * started and ended outside the target `ref` before calling `onDismiss`
 * This helps to avoid edge-cases of accidental dismissals when dragging or clicking near the edges.
 *
 * @param options UseDismissGuardOptions object including `refs`, `onDismiss`, and optional `disableGuard`.
 * @returns Pointer Event handlers (`onPointerDown`, `onPointerUp`) to include on the Overlay or backdrop.
 */
export const useDismissGuard = ({ refs, onDismiss, disableGuard = false }) => {
    const pointerStartedInside = useRef(false);
    const isInside = useCallback((target) => target instanceof Node &&
        [refs].flat().some((ref) => { var _a, _b; return (_b = (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.contains) === null || _b === void 0 ? void 0 : _b.call(_a, target); }), [refs]);
    const onPointerDown = useCallback((e) => {
        pointerStartedInside.current = isInside(e.target);
    }, [isInside]);
    const onPointerUp = useCallback((e) => {
        // Only handle left-click and taps not middle or right mouse buttons.
        if (e.button !== 0)
            return;
        const pointerEndedInside = isInside(e.target);
        if ((!pointerStartedInside.current && !pointerEndedInside) ||
            (disableGuard && !pointerEndedInside)) {
            onDismiss();
        }
    }, [disableGuard, isInside, onDismiss]);
    return {
        onPointerDown,
        onPointerUp
    };
};
//# sourceMappingURL=useDismissGuard.js.map